import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = [
    'output',
    'loading',
    'taskStatusLabel',
    'taskStatusDetails',
    'taskProgress',
    'completedDetails',
    'completedDetailsFailure',
    'inProgressDetails',
    'resultMessageBanner',
  ];

  connect() {
    this.fetchTaskStatus();
    this.interval = setInterval(() => this.fetchTaskStatus(), 3000);
  }

  async fetchTaskStatus() {
    const { taskStatusUrl } = this.element.dataset;
    if (!taskStatusUrl) {
      console.error('Task status URL is not provided.');
      return;
    }

    try {
      const response = await fetch(taskStatusUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch task status: ${response.statusText}`);
      }

      const data = await response.json();
      this.updateDataAttributes(data);

      if (this.isTaskCompleted(data.status)) {
        this.handleCompletedTask(data);
      } else {
        this.handleInProgressTask(data);
      }

      if (this.shouldFetchResults(data.status)) {
        this.fetchResults();
      }
    } catch (error) {
      console.error('Error fetching task status:', error);
    }
  }

  updateTaskProgress(data) {
    const progressPercentage = data.total_count === 0
      ? 0
      : (
        (100 * (parseFloat(data.success_count) + parseFloat(data.failed_count)))
        / parseFloat(data.total_count)
      ).toFixed(2);

    if (this.hasTaskProgressTarget) {
      this.taskProgressTarget.style.width = `${progressPercentage}%`;
    }
  }

  updateDataAttributes(data) {
    Object.keys(data).forEach((key) => {
      const camelizeKey = key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
      this.element.dataset[camelizeKey] = data[key];
    });
  }

  isTaskCompleted(status) {
    return ['done', 'failed', 'finalized'].includes(status);
  }

  hasFailures() {
    return parseFloat(this.element.dataset.failedCount) !== 0;
  }

  handleCompletedTask(data) {
    this.updateTaskProgress(data);
    this.updateTaskStatusLabel(this.element.dataset.completedLabel);
    this.showCompletedDetails();
    this.showResultMessageBanner();
    this.hideInProgressDetails();
    clearInterval(this.interval);
  }

  handleInProgressTask(data) {
    this.showTaskStatusDetails();
    this.updateTaskStatusLabel(this.element.dataset.inProgressLabel);
    this.updateTaskProgress(data);
    this.showInProgressDetails();
    this.hideResultMessageBanner();
  }

  showCompletedDetails() {
    if (this.hasFailures() && this.hasCompletedDetailsFailureTarget) {
      this.completedDetailsFailureTarget.innerHTML = this.element.dataset.completedDetailsFailure;
      this.completedDetailsFailureTarget.style.display = '';
    } else if (!this.hasFailures() && this.hasCompletedDetailsTarget) {
      this.completedDetailsTarget.innerHTML = this.element.dataset.completedDetails;
      this.completedDetailsTarget.style.display = '';
    }
  }

  showResultMessageBanner() {
    if (!this.hasResultMessageBannerTarget) return;

    const messageKey = this.hasFailures() ? 'completedDetailsFailure' : 'completedDetails';
    const message = this.element.dataset[messageKey];

    if (message) {
      this.resultMessageBannerTarget.innerHTML = message;
      this.resultMessageBannerTarget.className = this.hasFailures() ? 'alert alert-danger' : 'alert alert-info';
      this.resultMessageBannerTarget.style.display = '';
    }
  }

  hideResultMessageBanner() {
    if (this.hasResultMessageBannerTarget) {
      this.resultMessageBannerTarget.style.display = 'none';
    }
  }

  showTaskStatusDetails() {
    if (this.hasTaskStatusDetailsTarget) {
      this.taskStatusDetailsTarget.style.display = '';
    }
  }

  showInProgressDetails() {
    if (this.hasInProgressDetailsTarget) {
      this.inProgressDetailsTarget.innerHTML = this.element.dataset.inProgressDetails;
      this.inProgressDetailsTarget.style.display = '';
    }
  }

  hideInProgressDetails() {
    if (this.hasInProgressDetailsTarget) {
      this.inProgressDetailsTarget.style.display = 'none';
    }
  }

  updateTaskStatusLabel(label) {
    if (this.hasTaskStatusLabelTarget) {
      this.taskStatusLabelTarget.textContent = label;
    }
  }

  shouldFetchResults(status) {
    return status !== 'scheduled'
            && (parseFloat(this.element.dataset.successCount) !== 0
            || parseFloat(this.element.dataset.failedCount) !== 0);
  }

  async fetchResults() {
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute('content');

    try {
      const response = await fetch(this.element.dataset.url, {
        method: 'GET',
        headers: {
          Accept: 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
          'X-CSRF-Token': csrfToken, // Include CSRF token
        },
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const html = await response.text();
      if (this.hasOutputTarget && parseFloat(this.element.dataset.totalCount) > 0) {
        this.outputTarget.innerHTML = html;
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      if (this.hasOutputTarget) {
        this.outputTarget.insertAdjacentHTML(
          'beforeend',
          '<p>Error loading content.</p>',
        );
      }
    }
  }
}
